// /frontend/src/pages/HomePage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import ProductCard from '../components/product/ProductCard';
import { FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import { productService } from '../services/productService';

const HomePage = () => {
  const { categoryId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const searchQuery = searchParams.get('search') || '';
  
  const [filter, setFilter] = useState({
    category: categoryId || 'all',
    sort: 'recommended',
    showFilters: false
  });  
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Product state
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 12;
  
  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null); // Clear any previous errors
        
        const targetCategory = categoryId || 'all';
        console.log(`Fetching products for category: ${targetCategory}`);
        
        // First try to get products by category
        try {
          const response = await productService.getProductsByCategory(targetCategory);
          console.log('Category API response:', response);
          
          if (response && response.products) {
            setAllProducts(response.products);
          } else {
            // If no products array in response, set empty array
            setAllProducts([]);
            console.warn('No products found in response:', response);
          }
        } catch (categoryErr) {
          console.error('Error fetching by category:', categoryErr);
          
          // As a fallback, get all products and filter client-side
          const allResponse = await productService.getProducts();
          console.log('All products fallback response:', allResponse);
          
          if (allResponse && allResponse.products) {
            // If 'all' category, use all products
            if (targetCategory === 'all') {
              setAllProducts(allResponse.products);
            } else {
              // Otherwise filter client-side by category
              const filtered = allResponse.products.filter(p => p.category === targetCategory);
              setAllProducts(filtered);
            }
          } else {
            setAllProducts([]);
          }
        }
        
        // Update filter state to match current category
        setFilter(prev => ({ ...prev, category: targetCategory }));
        setLoading(false);
      } catch (err) {
        console.error('Final fetch error:', err);
        setError(err.message || 'Failed to fetch products');
        setAllProducts([]);
        setLoading(false);
      }
    };

    fetchProducts();
  }, [categoryId]);
  
  // Update filtered products when filter or search changes
  useEffect(() => {
    // Check if allProducts is an array before using spread operator
    if (!Array.isArray(allProducts)) {
      console.error("allProducts is not an array:", allProducts);
      setFilteredProducts([]);
      return;
    }
    
    let filtered = [...allProducts];

    // Only apply category filter if it's not 'all'
    if (filter.category && filter.category !== 'all') {
      filtered = filtered.filter(product => product.category === filter.category);
    }
    
    // Apply search filter if a search query exists
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(product => 
        (product.name && product.name.toLowerCase().includes(query)) || 
        (product.description && product.description.toLowerCase().includes(query)) ||
        (product.category && product.category.toLowerCase().includes(query))
      );
    }
    
    // Apply sorting
    switch (filter.sort) {
      case 'price-low':
        filtered.sort((a, b) => (a.salePrice || a.price) - (b.salePrice || b.price));
        break;
      case 'price-high':
        filtered.sort((a, b) => (b.salePrice || b.price) - (a.salePrice || a.price));
        break;
      case 'newest':
        filtered = filtered.filter(p => p.isNewArrival).concat(filtered.filter(p => !p.isNewArrival));
        break;
      default: // recommended
        filtered = filtered.filter(p => p.isBestSeller)
          .concat(filtered.filter(p => p.isNewArrival && !p.isBestSeller))
          .concat(filtered.filter(p => !p.isBestSeller && !p.isNewArrival));
    }
    
    setFilteredProducts(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [filter, allProducts, searchQuery]);
  
  // Update category filter when URL parameter changes
  useEffect(() => {
    if (categoryId) {
      setFilter(prev => ({ ...prev, category: categoryId }));
    }
  }, [categoryId]);
  
  // Function to clear search and navigate to current category without search parameter
  const clearSearch = () => {
    navigate(`/category/${filter.category}`);
  };
  
  const changePage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo(0, 0);
    }
  };
  
  // Calculate pagination
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  
  return (
    <Layout>
      {/* Show search results info if search query exists */}
      {searchQuery && (
        <div className="max-w-7xl mx-auto px-4 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="text-gray-700 flex items-center">
              <span>
                Search results for: <span className="font-medium text-teal-800">"{searchQuery}"</span>
                <span className="ml-2 text-sm text-gray-500">({filteredProducts.length} {filteredProducts.length === 1 ? 'result' : 'results'})</span>
              </span>
              <button
                onClick={clearSearch}
                className="ml-4 flex items-center text-teal-700 hover:text-teal-900 transition-colors"
                title="Clear search"
              >
                <FaTimes className="mr-1" /> Clear search
              </button>
            </div>
            
            {/* <div className="flex items-center space-x-4">
              <select 
                value={filter.sort}
                onChange={(e) => setFilter({...filter, sort: e.target.value})}
                className="border-gray-300 rounded-md text-sm py-1 px-2"
              >
                <option value="recommended">Recommended</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="newest">Newest</option>
              </select>
            </div> */}
          </div>
        </div>
      )}
      
      {/* Loading, Error and Empty States */}
      {loading ? (
        <div className="max-w-7xl mx-auto px-4 py-12 flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-800"></div>
        </div>
      ) : error ? (
        <div className="max-w-7xl mx-auto px-4 py-12 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Products</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="inline-block px-6 py-3 bg-teal-800 text-white rounded hover:bg-teal-900 transition-colors"
          >
            Try Again
          </button>
        </div>
      ) : (
        /* Product Grid */
        <div className="max-w-7xl mx-auto px-4 py-12" style={{ maxWidth: "90%", paddingLeft: "1rem", paddingRight: "1rem" }}>
          {/* {!searchQuery && (
            <div className="flex justify-end mb-6">
              <select 
                value={filter.sort}
                onChange={(e) => setFilter({...filter, sort: e.target.value})}
                className="border-gray-300 rounded-md text-sm py-1 px-2"
              >
                <option value="recommended">Recommended</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="newest">Newest</option>
              </select>
            </div>
          )} */}
          
          {filteredProducts.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {currentProducts.map((product) => (
                  <ProductCard key={product._id} product={product} />
                ))}
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center mt-12">
                  <nav className="flex items-center">
                    <button 
                      onClick={() => changePage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`p-2 rounded-l border ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'}`}
                    >
                      <FaChevronLeft className="h-4 w-4" />
                    </button>
                    
                    {[...Array(totalPages)].map((_, index) => (
                      <button
                        key={index}
                        onClick={() => changePage(index + 1)}
                        className={`px-4 py-2 border-t border-b ${
                          currentPage === index + 1
                            ? 'bg-teal-800 text-white'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {index + 1}
                      </button>
                    ))}
                    
                    <button 
                      onClick={() => changePage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`p-2 rounded-r border ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'}`}
                    >
                      <FaChevronRight className="h-4 w-4" />
                    </button>
                  </nav>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">No products found</h2>
              <p className="text-gray-600 mb-6">
                {searchQuery 
                  ? `We couldn't find any products matching "${searchQuery}". Try different keywords or browse all products.`
                  : "Try adjusting your filters or browse all products."}
              </p>
              {searchQuery ? (
                <button 
                  onClick={clearSearch}
                  className="inline-block px-6 py-3 bg-teal-800 text-white rounded hover:bg-teal-900 transition-colors mr-4"
                >
                  Clear search
                </button>
              ) : null}
              <a 
                href="/category/all"
                className="inline-block px-6 py-3 bg-teal-800 text-white rounded hover:bg-teal-900 transition-colors"
              >
                Browse all products
              </a>
            </div>
          )}
        </div>
      )}
    </Layout>
  );
};

export default HomePage;